import { ControlContainer, FormControl, NgModelGroup } from "@angular/forms";
import {Align, PropertyMetadata} from "@tylertech/tyler-api-types";
import {
    Utilities
} from "@tylertech/shared-module";
import {MetadataPropertyService, MetadataService, MetadataActionService} from "../../../../services";
import {AugmentedModelObject, RepositoryFormData, ValueChangedParms} from "../../../../interfaces";
import { Component, DoCheck, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, AfterViewInit, ViewChild, inject } from "@angular/core";
import { CheckboxComponent } from "@tylertech/forge";
import { KeyHandlerService, SecurityService } from "@tylertech/core-module";

@Component({
    selector: "metadata-input-checkbox",
    templateUrl: "./metadata-input-checkbox.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataInputCheckboxComponent implements OnInit, AfterViewInit, OnChanges, DoCheck
{
    modelGroup = inject(NgModelGroup);
    private $metadataService = inject(MetadataService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $securityService = inject(SecurityService);

    @Input()
        model: boolean;

    @Input()
        metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
        parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
        datacontroller: RepositoryFormData;

    @Input()
        viewOnly: boolean;

    @Input()
        propertyMetadata: PropertyMetadata;

    @Input()
        dense: boolean;

    @Input()
        forCellEdit: boolean;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
        hint: string;

    @Input()
        compact: boolean;

    @ViewChild("checkbox") checkbox: CheckboxComponent;

    isViewOnly: boolean;

    private _valueModel: boolean;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: boolean;

    private isInitialized: boolean;

    ngOnInit(): void
    {
        this.onModelChanged(this.model);
    }

    ngAfterViewInit(): void
    {
        if (this.isInitialized)
            return;
        this.isInitialized = true;
        if (this.checkbox && this.checkbox.checked !== this.model)
            this.checkbox.checked = this.model;
        this.modelGroup.control?.addControl(this.Name, new FormControl());
    }

    ngOnChanges(changesObj: SimpleChanges): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        const changedKeys = Object.keys(changesObj);
        for (const key of changedKeys)
        {
            const change = changesObj[key];
            switch (key)
            {
                case "model":
                    this.onModelChanged(change.currentValue);
                    break;
            }
        }
    }

    ngDoCheck(): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        if (this.unboundModel !== this.model)
        {
            this.onModelChanged(this.model);
        }

        const isBeingRecalculated = this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel);
        if (isBeingRecalculated && this.forCellEdit)
            this.delayFocus = true;
        else if (!isBeingRecalculated && this.delayFocus)
        {
            this.delayFocus = false;
            setTimeout(() => document.getElementById(this.ControlId)?.focus(), 50);
        }

        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent) ||
            isBeingRecalculated;
    }

    delayFocus: boolean = false;

    private onModelChanged(value: boolean): void
    {
        if (this.checkbox && this.checkbox.checked !== value)
            this.checkbox.checked = value;
        if (typeof value === "string") value = value === "true";
        this.unboundModel = value;
        // if model changed, check if we need to update valueModel or selectedItem.
        if (!value)
        {
            this.valueModel = null;
        }
        else if (!this.valueModel || this.valueModel !== value)
        {
            this.valueModel = value;
        }
    }

    get isDense(): boolean
    {
        return this.forCellEdit ? false : this.dense || this.compact;
    }

    get alignment(): string
    {
        if (this.forCellEdit)
        {
            return "center";
        }
        switch (this.propertyMetadata.Alignment)
        {
            case Align.Left:
                return "flex-start";
            case Align.Center:
                return "center";
            case Align.Right:
                return "flex-end";
            default:
                return "flex-start";
        }
    }

    get valueModel(): boolean
    {
        return this._valueModel;
    }

    set valueModel(value: boolean)
    {
        this._valueModel = value;
    }

    get isRequired(): boolean
    {
        return (
            this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent) &&
            !this.isViewOnly
        );
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    get ControlId(): string
    {
        if (Utilities.IsNullOrUndefined(this.metadataModel)) return `chk_${this.Name}`;
        let id = this.metadataModel.$controlId;
        if (!Utilities.IsNullOrUndefined(id)) return `chk_${this.Name}_${id}`;
        id = this.metadataModel.$controlId = this.metadataModel.Id;
        if (Utilities.IsNullOrUndefinedOrEmpty(id) || id === Utilities.emptyGuid)
        {
            id = this.metadataModel.$controlId = Utilities.generateRandomGuid();
        }
        return `chk_${this.Name}_${id}`;
    }

    get Label(): string
    {
        return this.forCellEdit
            ? ""
            : this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    fireValueChanged(): void
    {
        this.valueModel = this.checkbox.checked;
        if (this.propertyMetadata.ActionMetadata)
        {
            let controller = this.datacontroller;
            if (this.propertyMetadata.ActionMetadata.ServicePath === "NoServiceCall/") controller = null;
            this.$metadataActionService
                .ExecuteAction(this.metadataModel, this.propertyMetadata.ActionMetadata, controller)
                .then(
                    _r =>
                    {
                        // Action accepted, so fire away!
                        if (this.model === this.valueModel || !this.onValueChanged)
                        {
                            return;
                        }
                        this.onValueChanged.emit({ value: this.valueModel });
                    },
                    _r =>
                    {
                        //Action was cancelled so revert state
                        this.valueModel = !this.valueModel;
                    }
                );
        }
        else
        {
            this.onValueChanged.emit({ value: this.valueModel });
        }
    }

    handleKeyDown(event: KeyboardEvent): void
    {
        if(event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
    }
}
